import { useState, useEffect, useMemo } from 'react';
import { GenieExampleModal, GenieExample } from './GenieExampleModal';
import { useAgentPagesByUrlNames } from '@/hooks/agent/useAgentPage';
import { TwilioService } from '@/services/communication/TwilioService';

// Configuration for the After Hours examples - maps to real genies in the database
const GENIE_CONFIGS = [
  {
    urlSlug: 'emergency-plumber-assistant',
    phoneImageUrl: '/after-hours/RapidFlow Genie.png',
    fallbackName: 'RapidFlow',
    fallbackTagline: 'Streamlined Business Solutions',
    description: 'See how a plumbing service captures emergency calls and books appointments even when the office is closed. Perfect for urgent leak reports and routine maintenance scheduling.',
  },
  {
    urlSlug: 'office-pro-genie',
    phoneImageUrl: '/after-hours/OfficePro Genie.png',
    fallbackName: 'OfficePro',
    fallbackTagline: 'Professional Office Solutions',
    description: 'Experience how an office management service handles after-hours inquiries about office technology issues, equipment problems, and facility maintenance requests.',
  },
  {
    urlSlug: 'novotech',
    phoneImageUrl: '/after-hours/NovaTech Genie.png',
    fallbackName: 'NovaTech',
    fallbackTagline: 'Innovative Technology Solutions',
    description: 'Discover how a technology company captures inquiries and provides support even when the office is closed. Perfect for technical questions and product information requests.',
  },
  {
    urlSlug: 'horizon-genie',
    phoneImageUrl: '/after-hours/Horizon Stays.png',
    fallbackName: 'Horizon Stays',
    fallbackTagline: 'Vacation Rentals & Hospitality',
    description: 'Learn how a vacation rental company handles booking inquiries and guest support around the clock. This Genie assists with availability checks, amenity questions, and check-in instructions.',
  },
];

// Helper to get country flag emoji from country code
const getCountryFlag = (countryCode: string | null): string => {
  const flags: Record<string, string> = {
    US: '🇺🇸',
    AU: '🇦🇺',
    NZ: '🇳🇿',
    GB: '🇬🇧',
    CA: '🇨🇦',
  };
  return flags[countryCode || ''] || '🌍';
};

// Helper to get country name from country code
const getCountryName = (countryCode: string | null): string => {
  const names: Record<string, string> = {
    US: 'United States',
    AU: 'Australia',
    NZ: 'New Zealand',
    GB: 'United Kingdom',
    CA: 'Canada',
  };
  return names[countryCode || ''] || 'International';
};

export const AfterHoursGenieCTA = () => {
  const [selectedGenie, setSelectedGenie] = useState<GenieExample | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [genieExamples, setGenieExamples] = useState<GenieExample[]>([]);
  const [phoneNumbersMap, setPhoneNumbersMap] = useState<Record<string, any[]>>({});

  const urlSlugs = useMemo(() => GENIE_CONFIGS.map(c => c.urlSlug), []);
  const agentQueries = useAgentPagesByUrlNames(urlSlugs);

  const isLoading = agentQueries.some(q => q.isLoading);

  // Fetch phone numbers for each genie
  useEffect(() => {
    const fetchPhoneNumbers = async () => {
      const phoneMap: Record<string, any[]> = {};

      for (const query of agentQueries) {
        if (query.data) {
          const agentId = (query.data as any).agents?.id;
          if (agentId) {
            try {
              const phoneNumbers = await TwilioService.fetchAgentPhoneNumbers(agentId);
              phoneMap[query.data.url_name] = phoneNumbers;
            } catch (err) {
              console.error(`Error fetching phone numbers for ${query.data.url_name}:`, err);
            }
          }
        }
      }

      setPhoneNumbersMap(phoneMap);
    };

    if (!isLoading) {
      fetchPhoneNumbers();
    }
  }, [agentQueries, isLoading]);

  // Build genie examples from data
  useEffect(() => {
    const examples: GenieExample[] = [];

    agentQueries.forEach((query, index) => {
      const config = GENIE_CONFIGS[index];
      const agentPage = query.data;

      if (agentPage) {
        const phoneNumbers = phoneNumbersMap[agentPage.url_name] || [];
        const mappedPhoneNumbers = phoneNumbers.map(pn => ({
          country: getCountryName(pn.country_code),
          code: pn.country_code || 'US',
          number: pn.phone_number,
          flag: getCountryFlag(pn.country_code),
        }));

        const agentBranding = (agentPage as any).agents?.branding;
        const pageBranding = agentPage.branding as any;

        const logoUrl =
          agentBranding?.logoUrl ||
          agentBranding?.customAvatarUrl ||
          agentPage.genie_image_url ||
          pageBranding?.logoUrl ||
          pageBranding?.customAvatarUrl ||
          null;

        const agentName = (agentPage as any).agents?.name || agentPage.name;

        console.log(`[AfterHours] Loaded genie: ${config.urlSlug}`, {
          agentId: (agentPage as any).agents?.id,
          agentName,
          pageNname: agentPage.name,
          phoneNumbers: mappedPhoneNumbers,
          logoUrl,
        });

        examples.push({
          id: agentPage.url_name,
          name: agentName,
          tagline: (agentPage as any).agents?.description || config.fallbackTagline,
          description: config.description || `Experience how ${agentName} uses After Hours to capture leads and handle inquiries 24/7.`,
          logoUrl: logoUrl,
          phoneImageUrl: config.phoneImageUrl,
          brandColor: agentPage.brand_color || agentBranding?.primaryColor || pageBranding?.primaryColor || '#8267fc',
          phoneNumber: mappedPhoneNumbers[0]?.number || '',
          phoneNumbers: mappedPhoneNumbers.length > 0 ? mappedPhoneNumbers : undefined,
          webCallUrl: `/live-agent/${agentPage.url_name}`,
        });
      } else {
        // Use fallback data if agent not found
        examples.push({
          id: config.urlSlug,
          name: config.fallbackName,
          tagline: config.fallbackTagline,
          description: `Experience how ${config.fallbackName} uses After Hours to capture leads and handle inquiries 24/7.`,
          logoUrl: `/after-hours/examples/${config.urlSlug}-logo.png`,
          phoneImageUrl: config.phoneImageUrl,
          brandColor: '#8267fc',
          phoneNumber: '',
          webCallUrl: `/live-agent/${config.urlSlug}`,
        });
      }
    });

    setGenieExamples(examples);
  }, [agentQueries, phoneNumbersMap]);

  const handleGenieClick = (genie: GenieExample) => {
    setSelectedGenie(genie);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedGenie(null);
  };

  return (
    <section className="w-full py-16 sm:py-20 md:py-24 relative overflow-hidden bg-gradient-to-b from-slate-50/80 via-white to-white">
      {/* Subtle decorative top border */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-purple-200/50 to-transparent" />
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header - Centered */}
        <div className="text-center mb-10 md:mb-14">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-indigo-900 mb-4">
            Try an After Hours Genie Example
          </h2>
          <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
            Click on any example below to see how After Hours could work for your business — capturing more leads, increasing customer satisfaction, and ensuring no opportunity slips through the cracks.
          </p>
        </div>

        {/* Phone Images Grid */}
        <div className="flex justify-center items-end gap-2 sm:gap-4 md:gap-6 lg:gap-8">
          {isLoading ? (
            // Show placeholder images while loading
            GENIE_CONFIGS.map((config) => (
              <div
                key={config.urlSlug}
                className="flex-shrink-0 animate-pulse"
              >
                <img
                  src={config.phoneImageUrl}
                  alt="Loading..."
                  className="w-32 sm:w-40 md:w-52 lg:w-64 h-auto drop-shadow-xl opacity-50"
                />
              </div>
            ))
          ) : (
            genieExamples.map((genie) => (
              <button
                key={genie.id}
                onClick={() => handleGenieClick(genie)}
                className="flex-shrink-0 transition-all duration-300 hover:scale-105 hover:-translate-y-2 focus:outline-none focus:ring-4 focus:ring-purple-300 focus:ring-offset-2 rounded-3xl"
              >
                {/* Phone image */}
                <img
                  src={genie.phoneImageUrl}
                  alt={`${genie.name} - Click to connect`}
                  className="w-32 sm:w-40 md:w-52 lg:w-64 h-auto drop-shadow-xl hover:drop-shadow-2xl transition-all"
                />
              </button>
            ))
          )}
        </div>

        {/* Hint text */}
        <p className="text-center text-sm text-gray-400 mt-8">
          Click any phone to see connection options
        </p>
      </div>

      {/* Modal */}
      <GenieExampleModal
        genie={selectedGenie}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </section>
  );
};
